import { Header } from "semantic-ui-react";
import { useHistory, useParams } from "react-router-dom";
import { useEffect, useState } from "react";

import {
  InputImage,
  InputText,
  InputTextBox,
  InputToggle,
} from "../../../components/Inputs/Inputs";
import BackButton from "../../../components/BackButton/BackButton";
import { api } from "../../../api/api";
import objToFormdata from "../../../utils/objToFormdata";
import { useToast } from "../../../components/Toast";
import {
  bfirst_category_url,
  country_url,
  first_category_url,
  gfirst_category_url,
} from "../../../api/urls";
import unknownError from "../../../utils/unknownError";
import Loading from "../../../components/Loading/Loading";
import { moveUpToError } from "../../../utils/moveUpToError";
import { APIDropdown } from "../../../components/APIDropdown/APIDropdown";
import { modules, perms } from "../../../api/codes";
import { ApprovalText } from "../../../utils/approveLevel";
import { ProtectedElement } from "../../../components/ProtectedElement/ProtectedElement";
import { CheckCheck } from "lucide-react";

const initialState = {
  code: "",
  description: "",
  printable_name: "",
  disable: false,
  is_active: false,
  first: "",
  cat01_image: "",
  cat01_image_02: "",
  is_global: false,
  country: "",
};
const title = "First Category";
const module = modules.FirstCategory;

const NewFirstCategory = ({ edit, view, isGlobal, isB2B }) => {
  const backUrl = isB2B
    ? "/b2b_store/first_category"
    : isGlobal
    ? "/global_store/first_category"
    : "/masters/first_category";
  const apiUrl = isB2B
    ? bfirst_category_url
    : isGlobal
    ? gfirst_category_url
    : first_category_url;
  const [data, setData] = useState(initialState);
  const [errors, setErrors] = useState({});
  const history = useHistory();
  const params = useParams();
  const toast = useToast();
  const [isGetting, setIsGetting] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [aprvlLoader, setAprvlLoader] = useState(false);
  const [refresh, setRefresh] = useState(false);

  useEffect(() => {
    if (edit || view || params.id) {
      const getData = async () => {
        try {
          const res = await api.get(apiUrl + params.id + "/", {
            headers: {
              module: module,
            },
          });
          // res.data.cat01_image = await urlTofile(res.data.cat01_image);
          // res.data.cat01_image_02 = await urlTofile(res.data.cat01_image_02);
          setData(res.data);
          setIsGetting(false);
        } catch (err) {
          unknownError(err);
          setIsGetting(false);
        }
      };

      getData();
    } else {
      setIsGetting(false);
    }
  }, [params.id, edit, view, apiUrl, refresh]);

  const set = (key, value) => {
    setData((d) => {
      const newData = { ...d };
      newData[key] = value;
      return newData;
    });
  };

  if (isGetting) return <Loading />;

  return (
    <form
      id="form-wrapper"
      onSubmit={(e) => {
        e.preventDefault();
        setIsSaving(true);
        const newData = { ...data };
        if (isGlobal) {
          newData.is_global = true;
        }
        if (isB2B) {
          newData.is_b2b = true;
        }
        try {
          if (edit && params.id) {
            api
              .patch(apiUrl + data.id + "/", objToFormdata(newData), {
                headers: {
                  module: module,
                },
              })
              .then((res) => {
                toast.open("Edited data with id: " + res.data.id, "success");
                history.push(backUrl);
                setIsSaving(false);
              })
              .catch((err) => {
                if (err.response.data) {
                  setErrors(err.response.data);
                  moveUpToError();
                } else {
                  unknownError(err);
                }
                setIsSaving(false);
              });
          } else {
            api
              .post(apiUrl, objToFormdata(newData), {
                headers: {
                  module: module,
                },
              })
              .then((res) => {
                toast.open("Added data with id: " + res.data.id, "success");
                history.push(backUrl);
                setIsSaving(false);
              })
              .catch((err) => {
                if (err.response.data) {
                  setErrors(err.response.data);
                  moveUpToError();
                } else {
                  unknownError(err);
                }
                setIsSaving(false);
              });
          }
        } catch (err) {
          unknownError(err);
          setIsSaving(false);
        }
      }}
    >
      <div id="form">
        <BackButton href={backUrl} />
        <Header>{title}</Header>
        <InputText
          label="Code"
          placeholder="Code"
          disabled={view}
          value={data.code}
          error={errors["code"]}
          required
          onChange={(v) => set("code", v)}
        />
        <InputText
          label="Printable name"
          placeholder="Printable name"
          disabled={view}
          value={data.printable_name}
          error={errors["printable_name"]}
          required
          onChange={(v) => set("printable_name", v)}
        />
        <InputTextBox
          label="Description"
          placeholder="Description"
          disabled={view}
          value={data.description}
          error={errors["description"]}
          onChange={(v) => set("description", v)}
        />
        <InputText
          label="Category name"
          placeholder="Category name"
          disabled={view}
          value={data.first}
          error={errors["first"]}
          required
          onChange={(v) => set("first", v)}
        />
        <InputImage
          label="Category image 1"
          placeholder="Category image 1"
          disabled={view}
          value={data.cat01_image}
          error={errors["cat01_image"]}
          required
          onChange={(v) => set("cat01_image", v)}
        />
        <InputImage
          label="Category image 2"
          placeholder="Category image 2"
          disabled={view}
          value={data.cat01_image_02}
          error={errors["cat01_image_02"]}
          required
          onChange={(v) => set("cat01_image_02", v)}
        />
        <APIDropdown
          label="Country"
          placeholder="Country"
          required
          disabled={view}
          value={data.country}
          url={country_url}
          onChange={(v) => set("country", v)}
          error={errors["country"]}
          hasMultipleSelection
          module={module}
        />
        <InputToggle
          label="Active"
          disabled={view}
          value={data.is_active}
          error={errors["is_active"]}
          onChange={(v) => set("is_active", v)}
        />

        <ApprovalText
          id={params.id}
          level={data?.approval}
          setRefresh={setRefresh}
          setAprvlLoader={setAprvlLoader}
          toast={toast}
          view={view}
          aprvlLoader={aprvlLoader}
          module={module}
          apiUrl={apiUrl}
        />
        {/* new approval button */}

        {!view && (
          <>
            <div style={{ padding: "10px" }} />
            <div className="actions">
              <button
                className="btn-red"
                type="button"
                onClick={() => {
                  history.push(backUrl);
                }}
              >
                Cancel
              </button>
              <button
                className="btn"
                onClick={() => {
                  moveUpToError();
                }}
                disabled={isSaving}
              >
                Save
              </button>
            </div>
          </>
        )}
        <div style={{ padding: "10px" }} />
      </div>
    </form>
  );
};

export default NewFirstCategory;
